import { useEffect, useState } from 'react';
import client from '@api';
import {
  sharedQuery,
  sharedQueryKey,
  sharedQuerySeq,
} from '@utils/sharedQuery';
import { propertyListQuery } from '@graphql/queries/propertyList';
import type { PropertyListResponse, PropertyRecord } from './types';
import type { QueryState, RefetchableQueryState } from './hooks';

const PROPERTY_LIST_MAX_AGE_MS = 30_000;

export const prefetchProperties = (): Promise<PropertyRecord[]> =>
  loadProperties('', 'all', 'all', -1);

const loadProperties = (
  query: string,
  status: string,
  kind: string,
  refreshAfter: number,
): Promise<PropertyRecord[]> => {
  const filter = {
    ...(query.trim() ? { query: query.trim() } : {}),
    statuses:
      status === 'all'
        ? ['DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED']
        : [status.toUpperCase()],
    ...(kind !== 'all' ? { kinds: [kind.toUpperCase()] } : {}),
  };
  const variables = { first: 50, filter };
  return sharedQuery(
    sharedQueryKey('PropertyIntelligenceList', variables),
    () =>
      client()
        .query<PropertyListResponse>({
          query: propertyListQuery,
          variables,
          fetchPolicy: 'no-cache',
        })
        .then(({ data }) => data.properties.items),
    { maxAgeMs: PROPERTY_LIST_MAX_AGE_MS, after: refreshAfter },
  );
};

export const useProperties = ({
  query = '',
  status = 'all',
  kind = 'all',
}: {
  query?: string;
  status?: string;
  kind?: string;
} = {}): RefetchableQueryState<PropertyRecord[]> => {
  const [state, setState] = useState<QueryState<PropertyRecord[]>>({
    data: null,
    loading: true,
    error: null,
  });
  // Sequence mark of the last explicit refetch: results older than it are not
  // reused, while hooks refetching together still share one request.
  const [refreshAfter, setRefreshAfter] = useState(-1);

  useEffect(() => {
    let active = true;
    setState((previous) => ({
      data: previous.data,
      loading: !previous.data,
      error: null,
    }));
    loadProperties(query, status, kind, refreshAfter)
      .then((items) => {
        if (active) {
          setState({
            data: items,
            loading: false,
            error: null,
          });
        }
      })
      .catch((error: Error) => {
        if (active) setState({ data: null, loading: false, error });
      });
    return () => {
      active = false;
    };
  }, [kind, query, refreshAfter, status]);

  return {
    ...state,
    refetch: () => setRefreshAfter(sharedQuerySeq()),
  };
};
